/**
 * BalanceDesyncAuditTile — READ-ONLY admin widget.
 *
 * Lists every unpaid booking whose stored balance disagrees with what
 * the customer actually owes
 * (total − deposit credited − promo discount).
 *
 * Owner follow-up to the Amir Ali fix (6 Oct 2026): two write paths used
 * to move `total_price` without re-syncing the balance — the plain PUT,
 * and the Sunday-premium reschedule branch. Rows touched before that fix
 * still hold a wrong figure, and the collection email, Pay Balance page
 * and charge endpoint all read it.
 *
 * This widget never writes. Fixing a row means opening the booking and
 * using "Change services / price".
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, RefreshCw, ScaleIcon } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { API } from "../../App";

const money = (v) =>
  v === null || v === undefined
    ? "—"
    : `${Number(v) < 0 ? "-" : ""}€${Math.abs(Number(v)).toFixed(2)}`;

const ISSUE_LABEL = {
  overcharging: "Over-charging",
  undercharging: "Under-charging",
  missing_balance_fields: "No balance stored",
  fields_disagree: "Two fields disagree",
};

export const BalanceDesyncAuditTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [includeMissing, setIncludeMissing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/auth/admin/balance-desync-audit`, {
        params: { include_closed: includeClosed, include_missing: includeMissing },
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      const code = e?.response?.status;
      setError(code === 401 || code === 403
        ? "admin-only"
        : (e?.response?.data?.detail || e.message || "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [includeClosed, includeMissing]);

  useEffect(() => { load(); }, [load]);

  if (error === "admin-only") return null;

  const rows = data?.rows || [];
  const shown = expanded ? rows : rows.slice(0, 8);
  const s = data?.summary;

  return (
    <Card className="bg-slate-800/50 border-slate-700"
          data-testid="balance-desync-tile">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <ScaleIcon className="w-4 h-4 text-saturn-orange" />
            Balance Audit
            {data && (data.desynced === 0 ? (
              <Badge className="ml-1 bg-emerald-500/20 text-emerald-300 border-none text-xs"
                     data-testid="balance-desync-all-clear">All Correct</Badge>
            ) : (
              <Badge className="ml-1 bg-red-500/20 text-red-300 border-none text-xs"
                     data-testid="balance-desync-count">
                {data.desynced} to review
              </Badge>
            ))}
          </CardTitle>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-slate-400">
              <input type="checkbox" checked={includeClosed}
                     onChange={(e) => setIncludeClosed(e.target.checked)}
                     data-testid="balance-desync-include-closed" />
              Include cancelled
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-400">
              <input type="checkbox" checked={includeMissing}
                     onChange={(e) => setIncludeMissing(e.target.checked)}
                     data-testid="balance-desync-include-missing" />
              Include no-balance rows
            </label>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}
                    className="border-slate-600 text-slate-300 h-7"
                    data-testid="balance-desync-refresh">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Read-only. Unpaid bookings where the stored balance ≠ total −
          deposit paid − discount. To fix one, open it and use “Change
          services / price”.
        </p>
      </CardHeader>

      <CardContent>
        {error && (
          <p className="text-red-300 text-sm" data-testid="balance-desync-error">{error}</p>
        )}

        {s && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
            <div className="rounded bg-slate-900/60 p-2">
              <p className="text-slate-500">Over-charging</p>
              <p className="text-red-300 font-semibold"
                 data-testid="balance-desync-over">
                {s.overcharging_count} · {money(s.overcharging_total)}
              </p>
            </div>
            <div className="rounded bg-slate-900/60 p-2">
              <p className="text-slate-500">Under-charging</p>
              <p className="text-amber-300 font-semibold"
                 data-testid="balance-desync-under">
                {s.undercharging_count} · {money(s.undercharging_total)}
              </p>
            </div>
            <div className="rounded bg-slate-900/60 p-2">
              <p className="text-slate-500">No balance stored</p>
              <p className="text-slate-200 font-semibold"
                 title="Not a money risk — the balance helper derives the correct figure for these, so no surface quotes a wrong number.">
                {s.missing_balance_fields}
              </p>
            </div>
            <div className="rounded bg-slate-900/60 p-2">
              <p className="text-slate-500">Fields disagree</p>
              <p className="text-slate-200 font-semibold">{s.fields_disagree}</p>
            </div>
          </div>
        )}

        {data && data.desynced === 0 && !loading && (
          <p className="text-emerald-300 text-sm">
            Every unpaid booking’s balance matches what’s owed
            ({data.scanned} checked).
          </p>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-700">
                <tr>
                  <th className="text-left py-1.5 pr-3">Booking</th>
                  <th className="text-left py-1.5 pr-3">Customer</th>
                  <th className="text-left py-1.5 pr-3">Date</th>
                  <th className="text-right py-1.5 pr-3">Stored</th>
                  <th className="text-right py-1.5 pr-3">Correct</th>
                  <th className="text-right py-1.5 pr-3">Difference</th>
                  <th className="text-left py-1.5">Issue</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.booking_id}
                      className="border-b border-slate-800/70 hover:bg-slate-800/40"
                      data-testid={`balance-desync-row-${r.booking_id}`}>
                    <td className="py-1.5 pr-3 font-mono text-slate-400">
                      {String(r.booking_id).slice(-8)}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-200 truncate max-w-[9rem]">
                      {r.customer_name || "—"}
                    </td>
                    <td className="py-1.5 pr-3 text-slate-400 whitespace-nowrap">
                      {r.booking_date} {r.booking_time}
                    </td>
                    <td className="py-1.5 pr-3 text-right text-slate-300">
                      {money(r.stored_balance)}
                    </td>
                    <td className="py-1.5 pr-3 text-right text-white font-semibold">
                      {money(r.correct_balance)}
                    </td>
                    <td className={`py-1.5 pr-3 text-right font-semibold ${
                      r.difference > 0 ? "text-red-300"
                        : r.difference < 0 ? "text-amber-300" : "text-slate-400"}`}>
                      {r.difference === null ? "—"
                        : `${r.difference > 0 ? "+" : ""}${money(r.difference)}`}
                    </td>
                    <td className="py-1.5 text-slate-400">
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
                        {ISSUE_LABEL[r.issue] || r.issue}
                      </span>
                      {r.likely_cause?.length > 0 && (
                        <span className="text-[10px] text-slate-500">
                          {r.likely_cause.join(", ")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > shown.length && (
              <button onClick={() => setExpanded(true)}
                      className="mt-2 text-xs underline text-slate-300 hover:text-white"
                      data-testid="balance-desync-show-all">
                Show all {rows.length}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BalanceDesyncAuditTile;
